import { getPlatform } from "../../platform";
/**
 * OffchainAuthProvider wraps a real auth provider (e.g., Privy, Phantom) for the poofnet environment.
 *
 * For signMessageMock, this shows a custom confirmation modal and generates a mock signature.
 * This is used for offchain transaction signing. For real wallet signing, use signMessage
 * which delegates to the wrapped provider.
 */
export class OffchainAuthProvider {
    constructor(wrappedProvider, config = {}) {
        this.modalContainer = null;
        this.wrappedProvider = wrappedProvider;
        this.config = config;
    }
    // ============ Delegated Methods ============
    async login() {
        const user = await this.wrappedProvider.login();
        if (user) {
            return Object.assign(Object.assign({}, user), { provider: this });
        }
        return null;
    }
    // Forward per-call login overrides (theme/title/subtitle and the `method`
    // directive) to the wrapped provider. Without this, the top-level login()
    // forwarder (which duck-types `setLoginOverrides` on the active provider)
    // skips the wrapper and the override — including `method` — is dropped on
    // offchain/Poofnet builds.
    setLoginOverrides(opts) {
        var _a, _b;
        (_b = (_a = this.wrappedProvider).setLoginOverrides) === null || _b === void 0 ? void 0 : _b.call(_a, opts);
    }
    async logout() {
        await this.wrappedProvider.logout();
    }
    async restoreSession() {
        const user = await this.wrappedProvider.restoreSession();
        if (user) {
            return Object.assign(Object.assign({}, user), { provider: this });
        }
        return null;
    }
    async runTransaction(evmTransactionData, solTransactionData, options) {
        return this.wrappedProvider.runTransaction(evmTransactionData, solTransactionData, options);
    }
    async signTransaction(tx) {
        if (getPlatform().hasDOM) {
            await this.showUnsupportedTransactionModal();
        }
        throw new Error('Poofnet does not support real Solana transactions. Deploy your project to mainnet to use this feature.');
    }
    /**
     * Sign and submit transaction - not supported in poofnet environment.
     * See the real providers (PhantomWalletProvider, PrivyWalletProvider, SolanaKeypairProvider)
     * for the full implementation with blockhash handling and feePayer support.
     */
    async signAndSubmitTransaction(_transaction, _feePayer) {
        if (getPlatform().hasDOM) {
            await this.showUnsupportedTransactionModal();
        }
        throw new Error('Poofnet does not support real Solana transactions. Deploy your project to mainnet to use this feature.');
    }
    async getNativeMethods() {
        return this.wrappedProvider.getNativeMethods();
    }
    // Forward wallet export to the wrapped provider (only Privy embedded wallets
    // implement it). Duck-typed so wrappers around non-embedded providers throw
    // a clear error rather than a "not a function" crash.
    async exportWallet(address) {
        if (typeof this.wrappedProvider.exportWallet !== 'function') {
            throw new Error('Wallet export is not available for this wallet.');
        }
        await this.wrappedProvider.exportWallet(address);
    }
    // ============ signMessage delegates to wrapped provider ============
    async signMessage(message) {
        // Delegate to wrapped provider for real wallet signing
        return this.wrappedProvider.signMessage(message);
    }
    // ============ Mock signMessage with Custom Modal for Offchain Transactions ============
    async signMessageMock(message) {
        var _a, _b, _c, _d, _e, _f;
        // Callback: signing started
        (_b = (_a = this.config).onSigningStart) === null || _b === void 0 ? void 0 : _b.call(_a);
        try {
            // On RN (no DOM), auto-confirm — there's no modal to show.
            // On web, show the confirmation modal.
            if (getPlatform().hasDOM) {
                const confirmed = await this.showTransactionModal(message);
                if (!confirmed) {
                    throw new Error("Transaction rejected by user");
                }
            }
            // Generate mock signature (hash of message)
            const signature = await this.generateMockSignature(message);
            // Callback: signing complete
            (_d = (_c = this.config).onSigningComplete) === null || _d === void 0 ? void 0 : _d.call(_c, signature);
            return signature;
        }
        catch (error) {
            // Callback: signing error
            (_f = (_e = this.config).onSigningError) === null || _f === void 0 ? void 0 : _f.call(_e, error);
            throw error;
        }
    }
    // ============ Modal Implementation ============
    async showUnsupportedTransactionModal() {
        return new Promise((resolve) => {
            // Create modal container
            this.modalContainer = document.createElement("div");
            this.modalContainer.id = "poofnet-unsupported-modal";
            this.modalContainer.innerHTML = this.getUnsupportedModalHTML();
            document.body.appendChild(this.modalContainer);
            // Add styles
            const style = document.createElement("style");
            style.textContent = this.getModalStyles();
            this.modalContainer.appendChild(style);
            // Mark all other body children as inert
            document.body.childNodes.forEach((child) => {
                if (child !== this.modalContainer &&
                    child instanceof HTMLElement) {
                    child.setAttribute("inert", "");
                }
            });
            // Animate in
            requestAnimationFrame(() => {
                var _a, _b;
                const overlay = (_a = this.modalContainer) === null || _a === void 0 ? void 0 : _a.querySelector(".poofnet-modal-overlay");
                const content = (_b = this.modalContainer) === null || _b === void 0 ? void 0 : _b.querySelector(".poofnet-modal-content");
                if (overlay)
                    overlay.style.opacity = "1";
                if (content) {
                    content.style.opacity = "1";
                    content.style.transform = "translateY(0)";
                }
            });
            // Handle close button
            const closeBtn = this.modalContainer.querySelector("#poofnet-close-btn");
            closeBtn === null || closeBtn === void 0 ? void 0 : closeBtn.addEventListener("click", () => {
                this.closeModal();
                resolve();
            });
            // Handle cancel button
            const cancelBtn = this.modalContainer.querySelector("#poofnet-cancel-btn");
            cancelBtn === null || cancelBtn === void 0 ? void 0 : cancelBtn.addEventListener("click", () => {
                this.closeModal();
                resolve();
            });
            // Handle overlay click
            const overlay = this.modalContainer.querySelector(".poofnet-modal-overlay");
            overlay === null || overlay === void 0 ? void 0 : overlay.addEventListener("click", (e) => {
                if (e.target === overlay) {
                    this.closeModal();
                    resolve();
                }
            });
            // Handle escape key
            const escHandler = (e) => {
                if (e.key === "Escape") {
                    document.removeEventListener("keydown", escHandler);
                    this.closeModal();
                    resolve();
                }
            };
            document.addEventListener("keydown", escHandler);
        });
    }
    getUnsupportedModalHTML() {
        return `
      <div class="poofnet-modal-overlay">
        <div class="poofnet-modal-content">
          <button id="poofnet-close-btn" class="poofnet-close-btn" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="poofnet-modal-header">
            <div class="poofnet-modal-icon">⚠️</div>
            <h2 class="poofnet-modal-title">Transaction Not Supported</h2>
            <p class="poofnet-modal-subtitle">Poofnet Simulated Blockchain</p>
          </div>

          <div class="poofnet-modal-body">
            <div class="poofnet-info-box" style="background: #fef3c7;">
              <span class="poofnet-info-icon">ℹ️</span>
              <span class="poofnet-info-text" style="color: #92400e;">
                Poofnet is a simulated blockchain and does not support real Solana transactions.
                Deploy your project to mainnet to test real transactions.
              </span>
            </div>
          </div>

          <div class="poofnet-modal-footer">
            <button id="poofnet-cancel-btn" class="poofnet-btn poofnet-btn-cancel" style="flex: 1;">Close</button>
          </div>
        </div>
      </div>
    `;
    }
    async showTransactionModal(message) {
        return new Promise((resolve) => {
            // Parse the transaction message for display
            let parsed = {};
            try {
                parsed = JSON.parse(message);
            }
            catch (_a) {
                // If not JSON, just show raw message
            }
            // Create modal container
            this.modalContainer = document.createElement("div");
            this.modalContainer.id = "poofnet-tx-modal";
            this.modalContainer.innerHTML = this.getModalHTML(parsed, message);
            document.body.appendChild(this.modalContainer);
            // Add styles
            const style = document.createElement("style");
            style.textContent = this.getModalStyles();
            this.modalContainer.appendChild(style);
            // Mark all other body children as inert to disable focus traps from other modals
            document.body.childNodes.forEach((child) => {
                if (child !== this.modalContainer &&
                    child instanceof HTMLElement) {
                    child.setAttribute("inert", "");
                }
            });
            // Animate in
            requestAnimationFrame(() => {
                var _a, _b;
                const overlay = (_a = this.modalContainer) === null || _a === void 0 ? void 0 : _a.querySelector(".poofnet-modal-overlay");
                const content = (_b = this.modalContainer) === null || _b === void 0 ? void 0 : _b.querySelector(".poofnet-modal-content");
                if (overlay)
                    overlay.style.opacity = "1";
                if (content) {
                    content.style.opacity = "1";
                    content.style.transform = "translateY(0)";
                }
            });
            // Handle confirm
            const confirmBtn = this.modalContainer.querySelector("#poofnet-confirm-btn");
            confirmBtn === null || confirmBtn === void 0 ? void 0 : confirmBtn.addEventListener("click", () => {
                this.closeModal();
                resolve(true);
            });
            // Handle cancel
            const cancelBtn = this.modalContainer.querySelector("#poofnet-cancel-btn");
            cancelBtn === null || cancelBtn === void 0 ? void 0 : cancelBtn.addEventListener("click", () => {
                this.closeModal();
                resolve(false);
            });
            // Handle close button
            const closeBtn = this.modalContainer.querySelector("#poofnet-close-btn");
            closeBtn === null || closeBtn === void 0 ? void 0 : closeBtn.addEventListener("click", () => {
                this.closeModal();
                resolve(false);
            });
            // Handle overlay click
            const overlay = this.modalContainer.querySelector(".poofnet-modal-overlay");
            overlay === null || overlay === void 0 ? void 0 : overlay.addEventListener("click", (e) => {
                if (e.target === overlay) {
                    this.closeModal();
                    resolve(false);
                }
            });
            // Handle escape key
            const escHandler = (e) => {
                if (e.key === "Escape") {
                    document.removeEventListener("keydown", escHandler);
                    this.closeModal();
                    resolve(false);
                }
            };
            document.addEventListener("keydown", escHandler);
        });
    }
    closeModal() {
        if (this.modalContainer) {
            // Remove inert attribute from all elements
            document.body.querySelectorAll("[inert]").forEach((el) => {
                el.removeAttribute("inert");
            });
            const overlay = this.modalContainer.querySelector(".poofnet-modal-overlay");
            const content = this.modalContainer.querySelector(".poofnet-modal-content");
            if (overlay)
                overlay.style.opacity = "0";
            if (content) {
                content.style.opacity = "0";
                content.style.transform = "translateY(20px)";
            }
            setTimeout(() => {
                var _a;
                (_a = this.modalContainer) === null || _a === void 0 ? void 0 : _a.remove();
                this.modalContainer = null;
            }, 200);
        }
    }
    formatPath(path) {
        const parts = path.split("/").filter(Boolean);
        if (parts.length >= 2) {
            return {
                collection: parts[0],
                documentId: parts[parts.length - 1],
            };
        }
        return { collection: parts[0] || path, documentId: null };
    }
    formatFieldValue(value) {
        if (value === null || value === undefined)
            return "null";
        if (typeof value === "string") {
            return value.length > 30
                ? `"${value.slice(0, 30)}..."`
                : `"${value}"`;
        }
        if (typeof value === "number" || typeof value === "boolean")
            return String(value);
        if (Array.isArray(value))
            return `[${value.length} items]`;
        if (typeof value === "object")
            return `{${Object.keys(value).length} fields}`;
        return String(value);
    }
    getActionDescription(type, collection, isNew) {
        if (type === "delete")
            return `Delete from ${collection}`;
        return isNew ? `Create in ${collection}` : `Update ${collection}`;
    }
    getModalHTML(parsed, rawMessage) {
        const instructions = parsed.instructions || [];
        // Group instructions by type for summary
        const sets = instructions.filter((i) => i.type === "set");
        const deletes = instructions.filter((i) => i.type === "delete");
        const instructionsList = instructions
            .map((inst) => {
            const { collection, documentId } = this.formatPath(inst.path);
            const isDelete = inst.type === "delete";
            const icon = isDelete ? "🗑️" : "✏️";
            const actionClass = isDelete
                ? "poofnet-action-delete"
                : "poofnet-action-set";
            // Format data fields for display
            let fieldsHtml = "";
            if (inst.data && !isDelete) {
                const fields = Object.entries(inst.data)
                    .filter(([key]) => !key.startsWith("_")) // Hide internal fields
                    .slice(0, 4);
                if (fields.length > 0) {
                    fieldsHtml = `
            <div class="poofnet-fields">
              ${fields
                        .map(([key, value]) => `
                <div class="poofnet-field">
                  <span class="poofnet-field-key">${key}:</span>
                  <span class="poofnet-field-value">${this.formatFieldValue(value)}</span>
                </div>
              `)
                        .join("")}
              ${Object.keys(inst.data).filter((k) => !k.startsWith("_"))
                        .length > 4
                        ? `<div class="poofnet-field-more">+${Object.keys(inst.data).filter((k) => !k.startsWith("_")).length - 4} more fields</div>`
                        : ""}
            </div>
          `;
                }
            }
            return `
        <div class="poofnet-instruction ${actionClass}">
          <div class="poofnet-instruction-header">
            <span class="poofnet-instruction-icon">${icon}</span>
            <span class="poofnet-instruction-action">${isDelete ? "Delete" : "Save"}</span>
            <span class="poofnet-instruction-collection">${collection}</span>
          </div>
          ${documentId
                ? `<div class="poofnet-instruction-id">ID: ${documentId}</div>`
                : ""}
          ${fieldsHtml}
        </div>
      `;
        })
            .join("");
        // Create summary text
        let summaryText = "";
        if (sets.length > 0 && deletes.length > 0) {
            summaryText = `${sets.length} update${sets.length > 1 ? "s" : ""}, ${deletes.length} delete${deletes.length > 1 ? "s" : ""}`;
        }
        else if (sets.length > 0) {
            summaryText = `${sets.length} update${sets.length > 1 ? "s" : ""}`;
        }
        else if (deletes.length > 0) {
            summaryText = `${deletes.length} delete${deletes.length > 1 ? "s" : ""}`;
        }
        return `
      <div class="poofnet-modal-overlay">
        <div class="poofnet-modal-content">
          <button id="poofnet-close-btn" class="poofnet-close-btn" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="poofnet-modal-header">
            <div class="poofnet-modal-icon">🔐</div>
            <h2 class="poofnet-modal-title">Confirm Transaction</h2>
            <p class="poofnet-modal-subtitle">Poofnet Simulated Blockchain</p>
          </div>

          <div class="poofnet-modal-body">
            ${instructions.length > 0
            ? `
              ${summaryText
                ? `<div class="poofnet-summary">${summaryText}</div>`
                : ""}
              <div class="poofnet-instructions-list">
                ${instructionsList}
              </div>
            `
            : `
              <div class="poofnet-section">
                <h3 class="poofnet-section-title">Message</h3>
                <pre class="poofnet-raw-message">${rawMessage.slice(0, 200)}${rawMessage.length > 200 ? "..." : ""}</pre>
              </div>
            `}

            <div class="poofnet-info-box">
              <span class="poofnet-info-icon">ℹ️</span>
              <span class="poofnet-info-text">This is a simulated transaction. No real fees will be charged.</span>
            </div>
          </div>

          <div class="poofnet-modal-footer">
            <button id="poofnet-cancel-btn" class="poofnet-btn poofnet-btn-cancel">Cancel</button>
            <button id="poofnet-confirm-btn" class="poofnet-btn poofnet-btn-confirm">Confirm</button>
          </div>
        </div>
      </div>
    `;
    }
    getModalStyles() {
        return `
      /* Ensure inert elements remain visible while being non-interactive */
      [inert] {
        visibility: visible !important;
        opacity: 1 !important;
        display: unset !important;
      }

      .poofnet-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        opacity: 0;
        transition: opacity 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      .poofnet-modal-content {
        background: white;
        border-radius: 16px;
        width: 90%;
        max-width: 420px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.2s ease;
        max-height: 90vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        position: relative;
      }

      .poofnet-close-btn {
        position: absolute;
        top: 12px;
        right: 12px;
        width: 28px;
        height: 28px;
        border: none;
        background: #f3f4f6;
        border-radius: 8px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #6b7280;
        transition: all 0.15s ease;
        z-index: 1;
      }

      .poofnet-close-btn:hover {
        background: #e5e7eb;
        color: #374151;
      }

      .poofnet-modal-header {
        padding: 24px 24px 16px;
        text-align: center;
        border-bottom: 1px solid #f0f0f0;
        flex-shrink: 0;
      }

      .poofnet-modal-icon {
        font-size: 40px;
        margin-bottom: 8px;
      }

      .poofnet-modal-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .poofnet-modal-subtitle {
        margin: 4px 0 0;
        font-size: 13px;
        color: #8b5cf6;
        font-weight: 500;
      }

      .poofnet-modal-body {
        padding: 16px 20px;
        overflow-y: auto;
        flex: 1;
      }

      .poofnet-summary {
        font-size: 13px;
        color: #6b7280;
        margin-bottom: 12px;
        text-align: center;
      }

      .poofnet-section {
        margin-bottom: 16px;
      }

      .poofnet-section-title {
        font-size: 12px;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin: 0 0 8px;
      }

      .poofnet-instructions-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .poofnet-instruction {
        background: #f9fafb;
        border-radius: 10px;
        padding: 12px;
        border-left: 3px solid #8b5cf6;
      }

      .poofnet-instruction.poofnet-action-delete {
        border-left-color: #ef4444;
      }

      .poofnet-instruction-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;
      }

      .poofnet-instruction-icon {
        font-size: 16px;
        flex-shrink: 0;
      }

      .poofnet-instruction-action {
        font-size: 13px;
        font-weight: 600;
        color: #8b5cf6;
      }

      .poofnet-action-delete .poofnet-instruction-action {
        color: #ef4444;
      }

      .poofnet-instruction-collection {
        font-size: 13px;
        font-weight: 500;
        color: #374151;
      }

      .poofnet-instruction-id {
        font-size: 11px;
        color: #9ca3af;
        font-family: monospace;
        margin-bottom: 8px;
        word-break: break-all;
      }

      .poofnet-fields {
        background: white;
        border-radius: 6px;
        padding: 8px 10px;
        margin-top: 8px;
      }

      .poofnet-field {
        display: flex;
        gap: 8px;
        padding: 4px 0;
        font-size: 12px;
        border-bottom: 1px solid #f3f4f6;
      }

      .poofnet-field:last-child {
        border-bottom: none;
      }

      .poofnet-field-key {
        color: #6b7280;
        font-weight: 500;
        flex-shrink: 0;
      }

      .poofnet-field-value {
        color: #374151;
        word-break: break-all;
      }

      .poofnet-field-more {
        font-size: 11px;
        color: #9ca3af;
        padding-top: 4px;
        font-style: italic;
      }

      .poofnet-raw-message {
        background: #f9fafb;
        border-radius: 8px;
        padding: 12px;
        font-size: 11px;
        font-family: monospace;
        color: #4b5563;
        white-space: pre-wrap;
        word-break: break-all;
        margin: 0;
        max-height: 120px;
        overflow-y: auto;
      }

      .poofnet-info-box {
        display: flex;
        align-items: center;
        gap: 8px;
        background: #faf5ff;
        border-radius: 8px;
        padding: 10px 12px;
        margin-top: 12px;
      }

      .poofnet-info-icon {
        font-size: 14px;
        flex-shrink: 0;
      }

      .poofnet-info-text {
        font-size: 11px;
        color: #7c3aed;
        line-height: 1.4;
      }

      .poofnet-modal-footer {
        padding: 16px 20px 20px;
        display: flex;
        gap: 12px;
        flex-shrink: 0;
        border-top: 1px solid #f0f0f0;
      }

      .poofnet-btn {
        flex: 1;
        padding: 12px 16px;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s ease;
        border: none;
      }

      .poofnet-btn-cancel {
        background: #f3f4f6;
        color: #4b5563;
      }

      .poofnet-btn-cancel:hover {
        background: #e5e7eb;
      }

      .poofnet-btn-confirm {
        background: #8b5cf6;
        color: white;
      }

      .poofnet-btn-confirm:hover {
        background: #7c3aed;
      }
    `;
    }
    async generateMockSignature(message) {
        // Generate a deterministic signature using SHA-256 hash of message
        // This can be verified on the backend by hashing the same message
        const platform = getPlatform();
        const data = platform.textEncode(message);
        const hashBuffer = await platform.sha256(data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const base64 = platform.btoa(String.fromCharCode(...hashArray));
        return base64;
    }
}
